import { InjectionToken } from '@angular/core';
import { ShapeState } from '../../cross-team-shared/annotation/shared/state/shape-state';
import { TrackingEvent } from '../../cross-team-shared/event-tracking/event-utils';
import { RetryableRequestResult } from '../../cross-team-shared/misc-services/resources/url-request.service';
import { ToolName } from '../../cross-team-shared/tools-names';
import { ShapeGroupOrderState } from '../content/content-connector';
import { InFlightExtrapolationRequest } from '../content/in-flight-extrapolation-request';
import { ViewBox } from '../content/view-box';
import {
  Point2D,
  Point3D,
  PointType,
  ShapeExternal,
  ShapeType,
} from '../otterdam/answers/shape-typings';
import { EventTrackingOptions } from './event-tracking';
import { InteractionRestrictionType } from './interaction-modes';

export interface ShellConnector {
  /**
   * Opens a dialog to start adding feedback for a missing shape at the given 2D
   * or 3D imagePoint.
   */
  addMissingShapeFeedback(point: Point2D | Point3D): void;
  addClientFeedback(point: Point2D | Point3D): void;

  createShape(shape: ShapeState, method?: string): void;
  claimNextInternalId(): number;
  convertShapeStateToAnswer(
    shapeState: ShapeState<PointType, ShapeType>,
  ): ShapeExternal<PointType, ShapeType>;
  /**
   * May return undefined so that a content team can release code that handles a
   * shortcut before the core team releases the code that listens for it.
   */
  getHumanReadableShortcut(shortcutName: string): string | undefined;
  getMlAssistRange(): number;
  handleCanvasClick(): void;
  hasFeatureFlag(flag: string): boolean;
  hasPermission(permission: string): boolean;
  modifyShapeSelection(
    indexes: number[],
    setOrToggle?: 'set' | 'toggle',
    eventTrackingValue?: { source: string },
  ): void;
  notifySideViewRendererIsAllowed(isAllowed: boolean): void;
  onAssetLoadError(reason: string): void;

  /**
   * Called when the SVG viewBox value has been changed, to propagate that
   * change to any other content renderers that needs to stay in sync.
   */
  propagateViewBoxUpdate(outputName: string, viewBox: ViewBox): void;

  /**
   * Called when the padding around the inspected shapes has been changed, to
   * propagate that change to other content renderers to maintain inspect level.
   */
  propagateViewBoxPaddingUpdate(viewBoxPadding?: number): void;

  pushAndTrack(event: TrackingEvent, options?: EventTrackingOptions): void;
  setMousePosition(mousePosition: {
    clientPoint: Point2D;
    imagePoint: Point2D;
  }): void;
  setZoomPanelSettings(scale: number, enabled?: boolean): void;
  openCanvasContextMenu(position: Point2D): void;
  openShapeContextMenu(position: Point2D): void;
  scopeObject(shapeIndex: number): void;
  openZoomPanel(): void;
  toggleZoomPanel(): void;
  queueAssetRequest(
    frameNumber: number,
    sentRequest: () => Promise<RetryableRequestResult>,
  ): Promise<RetryableRequestResult>;
  restrictInteractions(mode: InteractionRestrictionType): VoidFunction;
  setFrameLoaded(frameNumber: number): void;
  setSelectedTool(name: ToolName, mode?: string): void;
  setShapes(shapeGroupOrderState: ShapeGroupOrderState): void;
  trackEvent(event: TrackingEvent, options?: EventTrackingOptions): void;
  updateInFlightMlExtrapolationRequests(
    mlRequests: InFlightExtrapolationRequest[],
  ): void;
}

export const shellConnectorToken = new InjectionToken<ShellConnector>(
  'ShellConnector',
);
